import { useEffect, useState } from "react";
import { usePublicContent } from "@/lib/useContent";
import { Loader } from "@/components/common/Loader";
import { SectionTitle } from "@/components/common/SectionTitle";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { MapPin, Navigation, ExternalLink, LocateFixed, Phone } from "lucide-react";
import { cn } from "@/lib/utils";

const mapsEmbed = (q) => `https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed`;
const mapsOpen = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
const dirLink = (origin, dest) =>
  `https://www.google.com/maps/dir/?api=1${origin ? `&origin=${encodeURIComponent(origin)}` : ""}&destination=${encodeURIComponent(dest)}`;

// Locations map + directions section.
// Detects the visitor's approximate origin via IP (ipwho.is, no key) and uses it
// as the default starting point inside a popup directions dialog.
export const LocationsDirections = () => {
  const { data, loading } = usePublicContent("locations");
  const [active, setActive] = useState(0);
  const [origin, setOrigin] = useState(null); // { label, coords }
  const [geoState, setGeoState] = useState("loading"); // loading | ready | error
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch("https://ipwho.is/")
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        if (j && j.success !== false && j.latitude) {
          setOrigin({
            label: [j.city, j.region].filter(Boolean).join(", ") || "Your area",
            coords: `${j.latitude},${j.longitude}`,
          });
          setGeoState("ready");
        } else setGeoState("error");
      })
      .catch(() => { if (!cancelled) setGeoState("error"); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <section className="bg-background py-16"><div className="mx-auto max-w-7xl px-4"><Loader /></div></section>
    );
  }
  if (!data || data.length === 0) return null;

  const loc = data[active] || data[0];
  const fullAddr = [loc.address, loc.city].filter(Boolean).join(", ") || loc.name;

  return (
    <section className="border-t border-border bg-background py-16 sm:py-20" data-testid="locations-directions">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
        <SectionTitle eyebrow="Find Us" title="Map & Directions" subtitle="Pick a facility, see it on the map, and get directions from your location." />

        {/* Facility selector */}
        <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Select facility">
          {data.map((l, i) => (
            <button
              key={l.id || i}
              type="button"
              role="tab"
              aria-selected={active === i}
              onClick={() => setActive(i)}
              data-testid={`directions-facility-${i}`}
              className={cn(
                "rounded-full border px-4 py-2 font-condensed text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 active:scale-[0.98]",
                active === i
                  ? "border-[hsl(var(--brand-red))] bg-brand text-white"
                  : "border-border text-muted-foreground hover:border-[hsl(var(--brand-red))] hover:text-foreground"
              )}
            >
              {l.name}
            </button>
          ))}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          <div className="overflow-hidden rounded-xl border border-border lg:col-span-2">
            <iframe
              key={fullAddr}
              title={`Map of ${loc.name}`}
              src={mapsEmbed(fullAddr)}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="h-[360px] w-full border-0"
              data-testid="directions-map"
            />
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="display-title text-2xl">{loc.name}</h3>
            <p className="mt-2 flex items-start gap-2 text-sm text-muted-foreground" data-testid="directions-address">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{fullAddr}
            </p>
            {loc.phone && (
              <p className="mt-1.5 flex items-center gap-2 text-sm text-muted-foreground"><Phone className="h-4 w-4 text-brand" />{loc.phone}</p>
            )}
            <p className="mt-4 flex items-start gap-2 text-xs text-muted-foreground" data-testid="directions-detected">
              <LocateFixed className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              {geoState === "ready"
                ? `Detected start: ${origin.label}`
                : geoState === "loading"
                ? "Detecting your area…"
                : "Location unavailable — Maps will ask for your start."}
            </p>
            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="directions-open-popup"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-brand px-5 py-3 text-sm font-semibold uppercase tracking-wide text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors active:scale-[0.98]"
              >
                <Navigation className="h-4 w-4" /> Get Directions
              </button>
              <a
                href={mapsOpen(fullAddr)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="directions-open-maps"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-border px-5 py-3 text-sm font-semibold uppercase tracking-wide hover:bg-accent transition-colors"
              >
                Open in Google Maps <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Directions popup */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="directions-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="display-title text-2xl">Directions to {loc.name}</DialogTitle>
            <DialogDescription>
              We use your approximate location (via IP) as the default starting point. You can change it in Google Maps.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-lg border border-border bg-muted/40 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your starting area</p>
              <p className="mt-1 flex items-center gap-2 font-medium">
                <LocateFixed className="h-4 w-4 text-brand" />
                {geoState === "ready" ? origin.label : "Not detected (set it in Maps)"}
              </p>
            </div>
            <div className="rounded-lg border border-border bg-muted/40 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Destination facility</p>
              <p className="mt-1 flex items-start gap-2 font-medium"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{fullAddr}</p>
            </div>
            <div className="overflow-hidden rounded-lg border border-border">
              <iframe title="Directions preview" src={mapsEmbed(fullAddr)} loading="lazy" className="h-48 w-full border-0" />
            </div>
          </div>
          <DialogFooter>
            <a
              href={dirLink(geoState === "ready" ? origin.coords : "", fullAddr)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="directions-go"
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand px-5 py-3 text-sm font-semibold uppercase tracking-wide text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors"
            >
              <Navigation className="h-4 w-4" /> Get Directions in Google Maps
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
